import { useFormik } from "formik"
import { CurrencyInput, Grid, Input, Modal, MultipleAutocomplete, MultipleAutocompleteOption } from "../../../../components"
import { useAxios, useToggle } from "../../../../hooks"
import { useEffect, useMemo } from "react"
import { CreateDepartmentValidationSchema } from "@next-pontos/validations"
import { enqueueSnackbar } from "notistack"

interface AddEditDepartmentModalProps {
    id?: string
    onCancel: () => void
    onSuccess: () => void
}

export const AddEditDepartmentModal = ({ id, onCancel, onSuccess}: AddEditDepartmentModalProps) => {
    const [isLoading, setIsLoading] = useToggle(false)

    const [users] = useAxios('/user')

    const [department, fetchDepartment] = useAxios({
        url: `/department/${id}`,
        method: 'GET'
    }, {
        manual: true
    })

    const [createdDepartment, createDepartment] = useAxios({
        url: '/department',
        method: 'POST'
    }, {
        manual: true
    })

    const [updatedDepartment, updateDepartment] = useAxios({
        url: `/department/${id}`,
        method: 'PUT'
    }, {
        manual: true
    })

    const formik = useFormik({
        initialValues: {
            name: '',
            manager_bonus: '',
            seller_bonus: '',
            manager_user_id: [] as number[],
            seller_user_id: [] as number[],
        },
        validationSchema: CreateDepartmentValidationSchema,
        onSubmit: async values => {
            setIsLoading.setTrue()
            const { status } = id ? await updateDepartment({ data: values }): await createDepartment({data: values})

            if (status === 200) {
                enqueueSnackbar(`Departament ${id ? 'atualizado' : 'criado'} com sucesso!`, {variant: 'success'})
                onSuccess()
            }
            setIsLoading.setFalse()
        }
    })

    const managers = useMemo(() => {
        return (users.data || []).filter((user: {role: string}) => user.role === 'SELLER' || user.role === 'ADMIN')
        .map((user: {name: string, id: number}) => ({
            label: user.name,
            value: user.id
        })).filter((user: {label: string, value: number}) => !formik.values.seller_user_id.includes(user.value))
    }, [formik.values.seller_user_id, users])

    const sellers = useMemo(() => {
        return (users.data || []).filter((user: {role: string}) => user.role === 'SELLER' || user.role === 'ADMIN')
        .map((user: {name: string, id: number}) => ({
            label: user.name,
            value: user.id
        })).filter((user: {label: string, value: number}) => !formik.values.manager_user_id.includes(user.value))
    }, [formik.values.manager_user_id, users])

    useEffect(() => {
        if (id) {
            (async () => {
                setIsLoading.setTrue()
                
                const { data } = await fetchDepartment()

                formik.resetForm({
                    values: {
                        name: data.name,
                        manager_bonus: Number(data.department_bonus[0].manager_bonus_order_percent) as unknown as string,
                        seller_bonus: Number(data.department_bonus[0].bonus_order_percent) as unknown as string,
                        manager_user_id: data.manager_department.map((user: { user_id: number }) => user.user_id),
                        seller_user_id: data.user_department.map((user: { user_id: number }) => user.user_id),
                    }
                })

                setIsLoading.setFalse()
            })()
        }
    }, [id])
    
    return (
        <Modal
            open
            title={`${id ? 'Adicionar' : 'Editar'} Departamento`}
            confirmLabel="Confirmar"
            onConfirm={formik.submitForm}
            cancelLabel="Cancelar"
            onCancel={onCancel}
            onClose={onCancel}
        >
            <form onSubmit={formik.submitForm}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Input 
                            name="name"
                            label="Nome"
                            value={formik.values.name}
                            helperText={formik.touched.name && formik.errors.name}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            error={!!formik.touched.name && !!formik.errors.name}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <CurrencyInput 
                            label="Comissão dos Gerentes (%)"
                            onChangeValue={(_, originalValue) => {
                                formik.setFieldValue("manager_bonus", originalValue);
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.manager_bonus}
                            error={!!formik.touched.manager_bonus && !!formik.errors.manager_bonus}
                            helperText={formik.touched.manager_bonus && formik.errors.manager_bonus}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <CurrencyInput 
                            label="Comissão dos Emissores (%)"
                            onChangeValue={(_, originalValue) => {
                                formik.setFieldValue("seller_bonus", originalValue);
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.seller_bonus}
                            error={!!formik.touched.seller_bonus && !!formik.errors.seller_bonus}
                            helperText={formik.touched.seller_bonus && formik.errors.seller_bonus}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MultipleAutocomplete 
                            label="Gerentes"
                            onChange={(value) => {
                                const users = (value as unknown as MultipleAutocompleteOption[]).map(item => item.value)

                                formik.setFieldValue('manager_user_id', users, true)
                                formik.setFieldValue('seller_user_id', formik.values.seller_user_id.filter(seller => !users.includes(seller)), true)
                            }}
                            options={managers}
                            value={formik.values.manager_user_id.map(item => {
                                const user = (managers || []).find((user: { value: number }) => user.value === item)

                                return user
                            })}
                            error={!!formik.touched.manager_user_id && !!formik.errors.manager_user_id}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MultipleAutocomplete 
                            label="Emissores"
                            onChange={(value) => {
                                const users = (value as unknown as MultipleAutocompleteOption[]).map(item => item.value)

                                formik.setFieldValue('seller_user_id', users, true)
                                formik.setFieldValue('manager_user_id', formik.values.manager_user_id.filter(seller => !users.includes(seller)), true)
                            }}
                            options={sellers}
                            value={formik.values.seller_user_id.map(item => {
                                const user = (sellers || []).find((user: { value: number }) => user.value === item)

                                return user
                            })}
                            error={!!formik.touched.manager_user_id && !!formik.errors.manager_user_id}
                        />
                    </Grid>
                </Grid>
            </form>
        </Modal>
    )
}